/**
 * DogmaProvider — Phase 5.2. Mirrors CostMetricsProvider pattern.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { logger } from 'src/logger';
import { TICKETS_FOR_OE_ERROR_URL } from 'src/constants/AppConstants';
import { useNotification } from './NotificationProvider';
import { FiltersStatus } from './SharedFiltersProvider';
import { FinTechOpsCategories, FinTechOpsMessages } from '../../FinTechOpsConstants';
import { DogmaDashboardItems, DogmaParsedDetails } from '../../dogma/DogmaInterface';
import { useDogmaData } from '../hooks/useDogmaData';

export interface DogmaContextValue {
  finTechOpsDogmaDataStatus: FiltersStatus;
  setFinTechOpsDogmaDataStatus: (s: FiltersStatus) => void;

  finTechOpsDogmaParsedData: DogmaParsedDetails;
  setFinTechOpsDogmaParsedData: (d: DogmaParsedDetails) => void;

  finTechOpsDogmaDashboardData: DogmaDashboardItems;
  setFinTechOpsDogmaDashboardData: (d: DogmaDashboardItems) => void;
}

const EMPTY_PARSED = {} as DogmaParsedDetails;
const EMPTY_DASHBOARD = {} as DogmaDashboardItems;

const defaultValue: DogmaContextValue = {
  finTechOpsDogmaDataStatus: 'loading',
  setFinTechOpsDogmaDataStatus: () => {},
  finTechOpsDogmaParsedData: EMPTY_PARSED,
  setFinTechOpsDogmaParsedData: () => {},
  finTechOpsDogmaDashboardData: EMPTY_DASHBOARD,
  setFinTechOpsDogmaDashboardData: () => {}
};

const DogmaContext = createContext<DogmaContextValue>(defaultValue);

export const useDogma = () => useContext(DogmaContext);

interface Props {
  children: React.ReactNode;
}

export const DogmaProvider = ({ children }: Props) => {
  const { data, loading, error } = useDogmaData();
  const { displayNotification } = useNotification();

  const [parsed, setParsed] = useState<DogmaParsedDetails>(EMPTY_PARSED);
  const [dashboard, setDashboard] = useState<DogmaDashboardItems>(EMPTY_DASHBOARD);
  const [status, setStatus] = useState<FiltersStatus>('loading');

  useEffect(() => {
    if (loading) {
      setStatus('loading');
      return;
    }
    if (error) {
      logger.error(`Unable to load ${FinTechOpsCategories.dogma} data`, error);
      setStatus('error');
      displayNotification('error', FinTechOpsMessages.APIError, TICKETS_FOR_OE_ERROR_URL);
      return;
    }
    if (data) {
      setParsed(data);
      setStatus('finished');
    }
  }, [loading, error, data, displayNotification]);

  const setFinTechOpsDogmaDataStatus = useCallback((s: FiltersStatus) => setStatus(s), []);
  const setFinTechOpsDogmaParsedData = useCallback((d: DogmaParsedDetails) => setParsed(d), []);
  const setFinTechOpsDogmaDashboardData = useCallback((d: DogmaDashboardItems) => setDashboard(d), []);

  const value = useMemo<DogmaContextValue>(
    () => ({
      finTechOpsDogmaDataStatus: status,
      setFinTechOpsDogmaDataStatus,
      finTechOpsDogmaParsedData: parsed,
      setFinTechOpsDogmaParsedData,
      finTechOpsDogmaDashboardData: dashboard,
      setFinTechOpsDogmaDashboardData
    }),
    [status, parsed, dashboard, setFinTechOpsDogmaDataStatus, setFinTechOpsDogmaParsedData, setFinTechOpsDogmaDashboardData]
  );

  return <DogmaContext.Provider value={value}>{children}</DogmaContext.Provider>;
};
